/**
 * PAAET - Public Authority for Applied Education and Training
 * الهيئة العامة للتعليم التطبيقي والتدريب
 * Data for the 2026/2027 admission cycle.
 *
 * Apply floors (`minHighSchool`) and department membership come from the
 * official 2026-2027 application PDFs (paaet-male-2027.pdf /
 * paaet-female-2027.pdf, نسب التقديم — modern-certificate column; the
 * PDFs state acceptance minimums will be published later). The training
 * sector (قطاع التدريب) tables publish علمي/أدبي floors — where they
 * differ the dept keeps the established _s/_l split or the lower (علمي)
 * value, matching the 2026 modeling convention.
 *
 * `equivalentRate` (last-year accepted) still carries the GA Semester 1
 * 2025/2026 accepted rates — the latest published acceptance data.
 * Departments new in 2026/2027 have `equivalentRate: 0` (no accepted-rate
 * history; the specialty card suppresses the last-year line).
 *
 * Certificate definitions (2026/2027): modern = issued 2023/2024 or later,
 * old = 2022/2023 or earlier.
 */

import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";

// ── Weight Templates ───────────────────────────────────────────────
export const collegeWeights: Record<string, CollegeWeights> = {
    basic_education: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 70,
        minEnglish: null,
        admissionType: "high_school",
    },
    business_studies: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 60,
        minEnglish: null,
        admissionType: "high_school",
    },
    health_sciences: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 70,
        minEnglish: null,
        admissionType: "high_school",
    },
    technological_studies: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 65,
        minEnglish: null,
        admissionType: "high_school",
    },
    nursing: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 70,
        minEnglish: null,
        admissionType: "high_school",
    },
    nursing_institute: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 60,
        minEnglish: null,
        admissionType: "high_school",
    },
    chemical_engineering: {
        highSchoolWeight: 70,
        arabicWeight: 0,
        chemistryWeight: 10,
        mathWeight: 10,
        englishWeight: 10,
        minHighSchool: 78,
        minEnglish: null,
        additionalConditions: "chemistry_aptitude_test",
        admissionType: "equivalent",
    },
    fire_engineering: {
        highSchoolWeight: 65,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 15,
        englishWeight: 20,
        minHighSchool: 85,
        minEnglish: null,
        admissionType: "equivalent",
    },
    special_training: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 50,
        minEnglish: null,
        admissionType: "high_school",
    },
    telecom_navigation: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 60,
        minEnglish: null,
        admissionType: "high_school",
    },
    energy_institute: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 55,
        minEnglish: null,
        admissionType: "high_school",
    },
    construction_institute: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 60,
        minEnglish: null,
        admissionType: "high_school",
    },
    industrial_sabah: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 50,
        minEnglish: null,
        admissionType: "high_school",
    },
    industrial_shuwaikh: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 55,
        minEnglish: null,
        admissionType: "high_school",
    },
    admin_services: {
        highSchoolWeight: 100,
        arabicWeight: 0,
        chemistryWeight: 0,
        mathWeight: 0,
        englishWeight: 0,
        minHighSchool: 50,
        minEnglish: null,
        admissionType: "high_school",
    },
};

// ── Departments ────────────────────────────────────────────────────

// =====================================================================
//  MALE (بنين) DEPARTMENTS
// =====================================================================

const maleDepartments: Department[] = [
    // ── Basic Education College (كلية التربية الأساسية) — Males ────
    // 2026/2027: floor 70 (modern) unchanged; اللغة العربية added; English
    // and French markers are now (3+4) — the first-or-second-choice
    // requirement was dropped.
    { code: "PAAET-M-BE01", collegeKey: "basic_education", departmentKey: "islamic_studies", equivalentRate: 85.01, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE02", collegeKey: "basic_education", departmentKey: "sciences", equivalentRate: 81.24, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE03", collegeKey: "basic_education", departmentKey: "mathematics", equivalentRate: 74.63, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE04", collegeKey: "basic_education", departmentKey: "arts_education", equivalentRate: 86.38, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE05", collegeKey: "basic_education", departmentKey: "music_education", equivalentRate: 79.7, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE06", collegeKey: "basic_education", departmentKey: "interior_design", equivalentRate: 84.38, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE07", collegeKey: "basic_education", departmentKey: "electricity", equivalentRate: 78.4, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE08", collegeKey: "basic_education", departmentKey: "information_studies", equivalentRate: 76.43, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE09", collegeKey: "basic_education", departmentKey: "educational_technology", equivalentRate: 78.71, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE10", collegeKey: "basic_education", departmentKey: "english_middle_school", equivalentRate: 70, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required"] },
    { code: "PAAET-M-BE11", collegeKey: "basic_education", departmentKey: "computer_teacher", equivalentRate: 74.65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-M-BE12", collegeKey: "basic_education", departmentKey: "french_language", equivalentRate: 85.03, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required"] },
    { code: "PAAET-M-BE13", collegeKey: "basic_education", departmentKey: "arabic_language", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_interview_required"] },

    // ── Business Studies College (كلية الدراسات التجارية) — Males ──
    // 2026/2027: floor 60 (modern); الحاسب الآلي is now علمي فقط (literary
    // variant dropped); الإحصاء التطبيقي dropped; التمويل والتقييم العقاري new.
    { code: "PAAET-M-BS01", collegeKey: "business_studies", departmentKey: "accounting", equivalentRate: 66.55, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS02", collegeKey: "business_studies", departmentKey: "insurance", equivalentRate: 60, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS03", collegeKey: "business_studies", departmentKey: "banking", equivalentRate: 62.77, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS04", collegeKey: "business_studies", departmentKey: "computer_science_s", equivalentRate: 63.26, type: "high_school", track: "scientific", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS06", collegeKey: "business_studies", departmentKey: "law", equivalentRate: 70.24, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS08", collegeKey: "business_studies", departmentKey: "admin_info_systems", equivalentRate: 60, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS09", collegeKey: "business_studies", departmentKey: "logistics", equivalentRate: 60, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS10", collegeKey: "business_studies", departmentKey: "human_resources", equivalentRate: 62.71, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS11", collegeKey: "business_studies", departmentKey: "marketing", equivalentRate: 60, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS12", collegeKey: "business_studies", departmentKey: "financial_management", equivalentRate: 60, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-BS13", collegeKey: "business_studies", departmentKey: "real_estate_finance", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 60 },

    // ── Health Sciences College (كلية العلوم الصحية) — Males ───────
    // 2026/2027: college floor rose to 70 (modern) for every department;
    // فني تخدير and علاج تنفسي dropped; علوم الأغذية والتغذية split into
    // two departments; pharmaceutical/emergency/labs marked (3+4+5+9) —
    // aptitude + interview + medical exam + equivalent-rate based.
    { code: "PAAET-M-HS01", collegeKey: "health_sciences", departmentKey: "medical_records", equivalentRate: 60, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },
    { code: "PAAET-M-HS02", collegeKey: "health_sciences", departmentKey: "pharmaceutical_sciences", equivalentRate: 91.56, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-M-HS03", collegeKey: "health_sciences", departmentKey: "medical_lab_technology", equivalentRate: 74.84, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-M-HS05", collegeKey: "health_sciences", departmentKey: "emergency_medical_tech", equivalentRate: 80.33, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-M-HS07", collegeKey: "health_sciences", departmentKey: "industrial_health_safety_m", equivalentRate: 73.04, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },
    { code: "PAAET-M-HS09", collegeKey: "health_sciences", departmentKey: "food_science", equivalentRate: 0, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },
    { code: "PAAET-M-HS10", collegeKey: "health_sciences", departmentKey: "nutrition_sciences", equivalentRate: 0, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },

    // ── Technological Studies (كلية الدراسات التكنولوجية) — Males ──
    // 2026/2027: هندسة الإطفاء returns as a bachelor program (floor 85,
    // modern-certificate only); تكنولوجيا التصنيع dropped; petroleum
    // exploration/production floors rose 65 → 75.
    { code: "PAAET-M-TS01", collegeKey: "technological_studies", departmentKey: "chemical_engineering_bsc", equivalentRate: 84.01, type: "high_school", track: "scientific", gender: "male", minHighSchool: 78, additionalConditions: ["paaet_first_or_second_choice", "paaet_equivalent_rate_based", "paaet_national_test_required"] },
    { code: "PAAET-M-TS02", collegeKey: "technological_studies", departmentKey: "chemical_engineering_comp", equivalentRate: 78, type: "high_school", track: "scientific", gender: "male", additionalConditions: ["paaet_refinery_chem_gpa"] },
    { code: "PAAET-M-TS03", collegeKey: "technological_studies", departmentKey: "mechanical_power", equivalentRate: 83.67, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },
    { code: "PAAET-M-TS05", collegeKey: "technological_studies", departmentKey: "automotive_mechanics", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS06", collegeKey: "technological_studies", departmentKey: "hvac_mechanics", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS07", collegeKey: "technological_studies", departmentKey: "marine_engineering_mechanics", equivalentRate: 68.01, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS08", collegeKey: "technological_studies", departmentKey: "welding_technology", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS09", collegeKey: "technological_studies", departmentKey: "electrical_machines", equivalentRate: 70.93, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS10", collegeKey: "technological_studies", departmentKey: "power_distribution", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS11", collegeKey: "technological_studies", departmentKey: "renewable_energy_tech", equivalentRate: 69.78, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS12", collegeKey: "technological_studies", departmentKey: "telecom_engineering", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS13", collegeKey: "technological_studies", departmentKey: "industrial_electronics", equivalentRate: 66.26, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS14", collegeKey: "technological_studies", departmentKey: "biomedical_equipment", equivalentRate: 73.62, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS15", collegeKey: "technological_studies", departmentKey: "building_construction", equivalentRate: 67.47, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS16", collegeKey: "technological_studies", departmentKey: "roads", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS17", collegeKey: "technological_studies", departmentKey: "surveying", equivalentRate: 65, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS18", collegeKey: "technological_studies", departmentKey: "refinery_operations", equivalentRate: 96.5, type: "high_school", track: "scientific", gender: "male", minHighSchool: 75, additionalConditions: ["paaet_first_choice", "paaet_age_limit", "paaet_not_disability_registered"] },
    { code: "PAAET-M-TS19", collegeKey: "technological_studies", departmentKey: "chemical_industries", equivalentRate: 84.1, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS20", collegeKey: "technological_studies", departmentKey: "applied_chemistry_labs", equivalentRate: 67.39, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS21", collegeKey: "technological_studies", departmentKey: "computer_engineering", equivalentRate: 73.29, type: "high_school", track: "scientific", gender: "male", minHighSchool: 65 },
    { code: "PAAET-M-TS22", collegeKey: "technological_studies", departmentKey: "petroleum_exploration", equivalentRate: 76.56, type: "high_school", track: "scientific", gender: "male", minHighSchool: 75 },
    { code: "PAAET-M-TS23", collegeKey: "technological_studies", departmentKey: "petroleum_production", equivalentRate: 82.35, type: "high_school", track: "scientific", gender: "male", minHighSchool: 75 },
    { code: "PAAET-M-TS24", collegeKey: "technological_studies", departmentKey: "energy_efficiency", equivalentRate: 71.02, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70 },
    { code: "PAAET-M-TS25", collegeKey: "technological_studies", departmentKey: "fire_engineering", equivalentRate: 0, type: "high_school", track: "scientific", gender: "male", minHighSchool: 85, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required", "paaet_age_limit", "paaet_not_disability_registered", "paaet_equivalent_rate_based", "paaet_national_test_required"] },

    // ── Nursing College (كلية التمريض) — Males ────────────────────
    // 2026/2027: all floors 70 (modern); every program marked (3+4+5+9).
    { code: "PAAET-M-NR01", collegeKey: "nursing", departmentKey: "nursing_bsc", equivalentRate: 90.3, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-M-NR02", collegeKey: "nursing", departmentKey: "school_nursing_bsc", equivalentRate: 83.67, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-M-NR03", collegeKey: "nursing", departmentKey: "nursing_diploma", equivalentRate: 80.45, type: "high_school", track: "scientific", gender: "male", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },

    // ── Special Training Courses (دورات تدريبية خاصة) — Males ─────
    // 2026/2027: الزراعة dropped; تفتيش خدمات بلدية نظافة new; percentages
    // replace the old pass-only rows (علمي column of the علمي/أدبي table).
    { code: "PAAET-M-ST01", collegeKey: "special_training", departmentKey: "labor_inspection", equivalentRate: 60.2, type: "high_school", gender: "male", minHighSchool: 60, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-M-ST02", collegeKey: "special_training", departmentKey: "science_lab_prep", equivalentRate: 66.47, type: "high_school", gender: "male", minHighSchool: 50 },
    { code: "PAAET-M-ST03", collegeKey: "special_training", departmentKey: "quran_memorization_l", equivalentRate: 60, type: "high_school", track: "literary", gender: "male", minHighSchool: 60, additionalConditions: ["paaet_first_choice", "paaet_interview_required"] },
    { code: "PAAET-M-ST03-S", collegeKey: "special_training", departmentKey: "quran_memorization_s", equivalentRate: 0, type: "high_school", track: "scientific", gender: "male", minHighSchool: 55, additionalConditions: ["paaet_first_choice", "paaet_interview_required"] },
    { code: "PAAET-M-ST05", collegeKey: "special_training", departmentKey: "veterinary", equivalentRate: 67.58, type: "high_school", gender: "male", minHighSchool: 50 },
    { code: "PAAET-M-ST06", collegeKey: "special_training", departmentKey: "municipal_cleanliness_inspection", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 65, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },

    // ── Higher Institute Telecom & Navigation — Males ─────────────
    { code: "PAAET-M-TN01", collegeKey: "telecom_navigation", departmentKey: "telecom_navigation_s", equivalentRate: 75.04, type: "high_school", track: "scientific", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-TN02", collegeKey: "telecom_navigation", departmentKey: "telecom_navigation_l", equivalentRate: 76.25, type: "high_school", track: "literary", gender: "male", minHighSchool: 70 },

    // ── Higher Institute Energy (المعهد العالي للطاقة) — Males only ─
    // 2026/2027: علمي فقط — the literary فصل تمهيدي is gone (أدبي = X).
    { code: "PAAET-M-EN01", collegeKey: "energy_institute", departmentKey: "energy_s", equivalentRate: 64.43, type: "high_school", track: "scientific", gender: "male", minHighSchool: 55 },

    // ── Construction Training Institute — Males only ──────────────
    { code: "PAAET-M-CT01", collegeKey: "construction_institute", departmentKey: "construction_prep", equivalentRate: 67.66, type: "high_school", gender: "male", minHighSchool: 60 },

    // ── Industrial Institute Sabah Al-Salem — Males only ──────────
    // 2026/2027: both diplomas are علمي-only (أدبي = X); فاحص مركبات floor
    // 50, معاين فني 60 with no printed footnotes.
    { code: "PAAET-M-IS01", collegeKey: "industrial_sabah", departmentKey: "vehicle_inspection_s", equivalentRate: 69.42, type: "high_school", track: "scientific", gender: "male", minHighSchool: 50 },
    { code: "PAAET-M-IS02", collegeKey: "industrial_sabah", departmentKey: "technician_s", equivalentRate: 60, type: "high_school", track: "scientific", gender: "male", minHighSchool: 60 },

    // ── Industrial Institute Shuwaikh — Males only ────────────────
    { code: "PAAET-M-IW01", collegeKey: "industrial_shuwaikh", departmentKey: "applied_arts_design", equivalentRate: 58.3, type: "high_school", gender: "male", minHighSchool: 55, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },

    // ── Higher Institute Administrative Services — Males ──────────
    // 2026/2027: ministry diplomas floor 50 (was pass-only); خدمات إدارية
    // and سكرتارية marked (11) — morning-period study; تنفيذ معاملات مراكز
    // new for males.
    { code: "PAAET-M-AS01", collegeKey: "admin_services", departmentKey: "social_services", equivalentRate: 57, type: "high_school", gender: "male", minHighSchool: 50 },
    { code: "PAAET-M-AS02", collegeKey: "admin_services", departmentKey: "secretarial", equivalentRate: 55, type: "high_school", gender: "male", minHighSchool: 55, additionalConditions: ["paaet_morning_study_only"] },
    { code: "PAAET-M-AS03", collegeKey: "admin_services", departmentKey: "admin_services_dept", equivalentRate: 55, type: "high_school", gender: "male", minHighSchool: 55, additionalConditions: ["paaet_morning_study_only"] },
    { code: "PAAET-M-AS04", collegeKey: "admin_services", departmentKey: "hotel_services", equivalentRate: 55, type: "high_school", gender: "male", minHighSchool: 50 },
    { code: "PAAET-M-AS05", collegeKey: "admin_services", departmentKey: "art_inspector", equivalentRate: 66.52, type: "high_school", gender: "male", minHighSchool: 50, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-M-AS06", collegeKey: "admin_services", departmentKey: "transaction_processing", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 50, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },

    // ── Nursing Institute (معهد التمريض - الشويخ) — Males (NEW 2026/2027) ─
    { code: "PAAET-M-NI01", collegeKey: "nursing_institute", departmentKey: "phlebotomy", equivalentRate: 0, type: "high_school", track: "scientific", gender: "male", minHighSchool: 60, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-M-NI02", collegeKey: "nursing_institute", departmentKey: "health_education", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 60 },
    { code: "PAAET-M-NI03", collegeKey: "nursing_institute", departmentKey: "sterilization", equivalentRate: 0, type: "high_school", gender: "male", minHighSchool: 60 },
];

// =====================================================================
//  FEMALE (بنات) DEPARTMENTS
// =====================================================================

const femaleDepartments: Department[] = [
    // ── Basic Education College (كلية التربية الأساسية) — Females ──
    // 2026/2027: floor 75 (modern) unchanged; العلوم، التربية الفنية،
    // التربية البدنية and رياض الأطفال dropped; English/French markers are
    // (3+4) — the first-or-second-choice requirement was dropped.
    { code: "PAAET-F-BE01", collegeKey: "basic_education", departmentKey: "arabic_language", equivalentRate: 85.32, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE03", collegeKey: "basic_education", departmentKey: "mathematics", equivalentRate: 82.46, type: "high_school", track: "scientific", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE06", collegeKey: "basic_education", departmentKey: "music_education", equivalentRate: 84.38, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE07", collegeKey: "basic_education", departmentKey: "interior_design", equivalentRate: 88.86, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE08", collegeKey: "basic_education", departmentKey: "electricity", equivalentRate: 89.11, type: "high_school", track: "scientific", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE09", collegeKey: "basic_education", departmentKey: "information_studies", equivalentRate: 84.22, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE10", collegeKey: "basic_education", departmentKey: "educational_technology", equivalentRate: 83.84, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE11", collegeKey: "basic_education", departmentKey: "english_primary_f", equivalentRate: 75, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required"] },
    { code: "PAAET-F-BE12", collegeKey: "basic_education", departmentKey: "computer_teacher", equivalentRate: 84.81, type: "high_school", track: "scientific", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },
    { code: "PAAET-F-BE13", collegeKey: "basic_education", departmentKey: "french_language", equivalentRate: 91.67, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required"] },
    { code: "PAAET-F-BE14", collegeKey: "basic_education", departmentKey: "home_economics_f", equivalentRate: 89.01, type: "high_school", gender: "female", minHighSchool: 75, additionalConditions: ["paaet_interview_required"] },

    // ── Business Studies College (كلية الدراسات التجارية) — Females ─
    // 2026/2027: floor 65 (modern), computer 60 علمي فقط (literary variant
    // dropped); الإحصاء التطبيقي dropped; التمويل والتقييم العقاري new.
    { code: "PAAET-F-BS01", collegeKey: "business_studies", departmentKey: "accounting", equivalentRate: 65.04, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS02", collegeKey: "business_studies", departmentKey: "insurance", equivalentRate: 65, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS03", collegeKey: "business_studies", departmentKey: "banking", equivalentRate: 67.57, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS04", collegeKey: "business_studies", departmentKey: "computer_science_s", equivalentRate: 63.67, type: "high_school", track: "scientific", gender: "female", minHighSchool: 60 },
    { code: "PAAET-F-BS06", collegeKey: "business_studies", departmentKey: "law", equivalentRate: 71.6, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS08", collegeKey: "business_studies", departmentKey: "admin_info_systems", equivalentRate: 72.44, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS09", collegeKey: "business_studies", departmentKey: "logistics", equivalentRate: 65.29, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS10", collegeKey: "business_studies", departmentKey: "human_resources", equivalentRate: 74.73, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS11", collegeKey: "business_studies", departmentKey: "marketing", equivalentRate: 69.53, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS12", collegeKey: "business_studies", departmentKey: "financial_management", equivalentRate: 65, type: "high_school", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-BS13", collegeKey: "business_studies", departmentKey: "real_estate_finance", equivalentRate: 0, type: "high_school", gender: "female", minHighSchool: 65 },

    // ── Health Sciences College (كلية العلوم الصحية) — Females ─────
    // 2026/2027: college floor rose to 70 (modern); فني تخدير and علاج
    // تنفسي dropped; علوم الأغذية والتغذية split; علوم البيئة التطبيقية is
    // now a yearly bachelor program; clinical diplomas marked (3+4+5+7) —
    // aptitude + interview + medical exam + equivalent-rate based.
    { code: "PAAET-F-HS01", collegeKey: "health_sciences", departmentKey: "medical_records", equivalentRate: 60, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70 },
    { code: "PAAET-F-HS02", collegeKey: "health_sciences", departmentKey: "pharmaceutical_sciences", equivalentRate: 91.69, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-HS03", collegeKey: "health_sciences", departmentKey: "medical_lab_technology", equivalentRate: 85.21, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-HS05", collegeKey: "health_sciences", departmentKey: "emergency_medical_tech", equivalentRate: 76.04, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-HS06", collegeKey: "health_sciences", departmentKey: "dental_clinic_technician_f", equivalentRate: 76.97, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-HS08", collegeKey: "health_sciences", departmentKey: "oral_dental_health_f", equivalentRate: 71.99, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-HS09", collegeKey: "health_sciences", departmentKey: "environmental_sciences_f", equivalentRate: 73.44, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70 },
    { code: "PAAET-F-HS11", collegeKey: "health_sciences", departmentKey: "nutrition_sciences", equivalentRate: 0, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70 },
    { code: "PAAET-F-HS12", collegeKey: "health_sciences", departmentKey: "food_science", equivalentRate: 0, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70 },

    // ── Technological Studies (كلية الدراسات التكنولوجية) — Females ─
    // 2026/2027: معدات طبية حيوية dropped for females; floor 65 unchanged.
    { code: "PAAET-F-TS01", collegeKey: "technological_studies", departmentKey: "renewable_energy_tech", equivalentRate: 65.41, type: "high_school", track: "scientific", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-TS02", collegeKey: "technological_studies", departmentKey: "telecom_engineering", equivalentRate: 65, type: "high_school", track: "scientific", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-TS04", collegeKey: "technological_studies", departmentKey: "applied_physics_labs_f", equivalentRate: 65, type: "high_school", track: "scientific", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-TS05", collegeKey: "technological_studies", departmentKey: "applied_chemistry_labs", equivalentRate: 67.83, type: "high_school", track: "scientific", gender: "female", minHighSchool: 65 },
    { code: "PAAET-F-TS06", collegeKey: "technological_studies", departmentKey: "computer_engineering", equivalentRate: 69.97, type: "high_school", track: "scientific", gender: "female", minHighSchool: 65 },

    // ── Nursing College (كلية التمريض) — Females ──────────────────
    // 2026/2027: all floors 70 (modern); every program marked (3+4+5+7).
    { code: "PAAET-F-NR01", collegeKey: "nursing", departmentKey: "nursing_bsc", equivalentRate: 88.6, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-NR02", collegeKey: "nursing", departmentKey: "school_nursing_bsc", equivalentRate: 82.16, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },
    { code: "PAAET-F-NR03", collegeKey: "nursing", departmentKey: "nursing_diploma", equivalentRate: 75.66, type: "high_school", track: "scientific", gender: "female", minHighSchool: 70, additionalConditions: ["paaet_aptitude_test", "paaet_interview_required", "paaet_medical_exam", "paaet_equivalent_rate_based"] },

    // ── Special Training Courses — Females ────────────────────────
    // 2026/2027: الزراعة and بيطرة dropped; تحفيظ قرآن gains a scientific
    // variant; تفتيش خدمات بلدية نظافة new; percentages replace pass-only.
    { code: "PAAET-F-ST01", collegeKey: "special_training", departmentKey: "labor_inspection", equivalentRate: 60, type: "high_school", gender: "female", minHighSchool: 60, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-F-ST02", collegeKey: "special_training", departmentKey: "science_lab_prep", equivalentRate: 79.12, type: "high_school", gender: "female", minHighSchool: 50 },
    { code: "PAAET-F-ST03", collegeKey: "special_training", departmentKey: "quran_memorization_l", equivalentRate: 60, type: "high_school", track: "literary", gender: "female", minHighSchool: 60, additionalConditions: ["paaet_first_choice", "paaet_interview_required"] },
    { code: "PAAET-F-ST03-S", collegeKey: "special_training", departmentKey: "quran_memorization_s", equivalentRate: 0, type: "high_school", track: "scientific", gender: "female", minHighSchool: 55, additionalConditions: ["paaet_first_choice", "paaet_interview_required"] },
    { code: "PAAET-F-ST06", collegeKey: "special_training", departmentKey: "municipal_cleanliness_inspection", equivalentRate: 0, type: "high_school", gender: "female", minHighSchool: 65, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },

    // ── Higher Institute Telecom & Navigation — Females ───────────
    { code: "PAAET-F-TN01", collegeKey: "telecom_navigation", departmentKey: "telecom_navigation_s", equivalentRate: 64.68, type: "high_school", track: "scientific", gender: "female", minHighSchool: 60 },
    { code: "PAAET-F-TN02", collegeKey: "telecom_navigation", departmentKey: "telecom_navigation_l", equivalentRate: 70, type: "high_school", track: "literary", gender: "female", minHighSchool: 70 },

    // ── Higher Institute Administrative Services — Females ────────
    // 2026/2027: ministry diplomas floor 50 (was pass-only); خدمات إدارية
    // and سكرتارية marked (6) — morning-period study.
    { code: "PAAET-F-AS01", collegeKey: "admin_services", departmentKey: "social_services", equivalentRate: 69.37, type: "high_school", gender: "female", minHighSchool: 50 },
    { code: "PAAET-F-AS02", collegeKey: "admin_services", departmentKey: "secretarial", equivalentRate: 64.74, type: "high_school", gender: "female", minHighSchool: 55, additionalConditions: ["paaet_morning_study_only"] },
    { code: "PAAET-F-AS03", collegeKey: "admin_services", departmentKey: "admin_services_dept", equivalentRate: 62.82, type: "high_school", gender: "female", minHighSchool: 55, additionalConditions: ["paaet_morning_study_only"] },
    { code: "PAAET-F-AS04", collegeKey: "admin_services", departmentKey: "hotel_services", equivalentRate: 55.68, type: "high_school", gender: "female", minHighSchool: 50 },
    { code: "PAAET-F-AS05", collegeKey: "admin_services", departmentKey: "transaction_processing", equivalentRate: 55, type: "high_school", gender: "female", minHighSchool: 50, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-F-AS06", collegeKey: "admin_services", departmentKey: "art_inspector", equivalentRate: 60.31, type: "high_school", gender: "female", minHighSchool: 50, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },

    // ── Nursing Institute (معهد التمريض - الشويخ) — Females (NEW 2026/2027) ─
    { code: "PAAET-F-NI01", collegeKey: "nursing_institute", departmentKey: "phlebotomy", equivalentRate: 0, type: "high_school", track: "scientific", gender: "female", minHighSchool: 60, additionalConditions: ["paaet_first_or_second_choice", "paaet_interview_required"] },
    { code: "PAAET-F-NI02", collegeKey: "nursing_institute", departmentKey: "health_education", equivalentRate: 0, type: "high_school", gender: "female", minHighSchool: 60 },
    { code: "PAAET-F-NI03", collegeKey: "nursing_institute", departmentKey: "sterilization", equivalentRate: 0, type: "high_school", gender: "female", minHighSchool: 60 },
];

// ── Combined departments ──────────────────────────────────────────
export const departments: Department[] = [...maleDepartments, ...femaleDepartments];

// ── Build colleges filtered by gender ─────────────────────────────
const allCollegeKeys = [
    { key: "basic_education", emoji: "📚" },
    { key: "business_studies", emoji: "💼" },
    { key: "health_sciences", emoji: "🏥" },
    { key: "technological_studies", emoji: "🔧", track: "scientific" as const },
    { key: "nursing", emoji: "👩‍⚕️", track: "scientific" as const },
    { key: "nursing_institute", emoji: "💉" },
    { key: "special_training", emoji: "📋" },
    { key: "telecom_navigation", emoji: "📡" },
    { key: "energy_institute", emoji: "⚡" },
    { key: "construction_institute", emoji: "🏗️" },
    { key: "industrial_sabah", emoji: "🏭" },
    { key: "industrial_shuwaikh", emoji: "⚙️" },
    { key: "admin_services", emoji: "🏢" },
];

function buildColleges(gender?: Gender): College[] {
    return allCollegeKeys
        .map((c) => {
            const depts = departments.filter(
                (d) => d.collegeKey === c.key && (!gender || d.gender === gender),
            );
            if (depts.length === 0) return null;
            return {
                key: c.key,
                emoji: c.emoji,
                ...(c.track ? { track: c.track } : {}),
                weights: collegeWeights[c.key],
                departments: depts,
            } as College;
        })
        .filter((c): c is College => c !== null);
}

// Default: all colleges (no gender filter)
export const colleges: College[] = buildColleges();

export function getCollegesForGender(gender: Gender): College[] {
    return buildColleges(gender);
}

// ── Helper Functions ───────────────────────────────────────────────
export function getCollegeMinRate(
    collegeKey: string,
    gender?: Gender,
    _track?: Track | null,
): number {
    const collegeDepts = departments.filter(
        (d) => d.collegeKey === collegeKey && (!gender || d.gender === gender),
    );
    if (collegeDepts.length === 0) return 0;
    return Math.min(...collegeDepts.map((d) => d.equivalentRate));
}

export function getRequiredFields(weights: CollegeWeights): string[] {
    const fields: string[] = ["high_school"];
    if (weights.englishWeight > 0) fields.push("english");
    if (weights.mathWeight > 0) fields.push("math");
    if (weights.arabicWeight > 0) fields.push("arabic");
    if (weights.chemistryWeight > 0) fields.push("chemistry");
    return fields;
}

export function getCollegeSpecialties(
    collegeKey: string,
    gender?: Gender,
    _track?: Track | null,
): Department[] {
    return departments.filter(
        (dept, index, self) =>
            dept.collegeKey === collegeKey &&
            dept.departmentKey !== null &&
            (!gender || dept.gender === gender) &&
            self.findIndex(
                (d) =>
                    d.departmentKey === dept.departmentKey &&
                    d.collegeKey === collegeKey &&
                    (!gender || d.gender === gender),
            ) === index,
    );
}

export function hasSelectableSpecialties(
    collegeKey: string,
    gender?: Gender,
    _track?: Track | null,
): boolean {
    return getCollegeSpecialties(collegeKey, gender).length > 1;
}

/* ── PAAET equivalent-rate calculation (isolated) ────────────────────────
 * Owned by this module so PAAET's formula (and the gender-aware admission
 * data already in this file) can never be touched by edits to any other
 * university.
 */
export function calculateEquivalentRate(weights: CollegeWeights, scores: Scores): number {
    if (weights.admissionType === "high_school") {
        return scores.high_school;
    }
    const rate =
        (scores.high_school * weights.highSchoolWeight) / 100 +
        ((scores.english || 0) * weights.englishWeight) / 100 +
        ((scores.math || 0) * weights.mathWeight) / 100 +
        ((scores.arabic || 0) * weights.arabicWeight) / 100 +
        ((scores.chemistry || 0) * weights.chemistryWeight) / 100;
    return Math.round(rate * 100) / 100;
}

export function meetsMinHighSchool(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minHighSchool === null) return true;
    return scores.high_school >= weights.minHighSchool;
}

export function meetsMinEnglish(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minEnglish === null) return true;
    return (scores.english || 0) >= weights.minEnglish;
}

/* ── Per-section public API (consumed via the registry) ─────────────────
 * PAAET filters by gender, not track. Track helpers return null/fallback.
 */

export function getTrackMinHighSchool(_track: Track | null | undefined): number | null {
    return null;
}

/** PAAET has no track-level HS minimum, so each college's minHighSchool
 *  is the hard apply-floor. */
export function isCollegeMinHardGate(): boolean {
    return true;
}

export function getDepartmentThreshold(dept: Department): number {
    return dept.equivalentRate;
}

export function getDisplayedMinRate(
    _dept: Department | null,
    _track: Track | null | undefined,
    _weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    return fallbackMinRate;
}

export function getDisplayedMinRateLabelKey(weights: CollegeWeights): string {
    return weights.admissionType === "high_school"
        ? "dream_university_paaet_last_year_hs_rate"
        : "dream_university_last_year_rate";
}

/** Specialty-step info alert: the college apply-floor (removed from the
 *  college grid) surfaces here beside the college name, with a note that the
 *  listed minimums apply to recent (new) high school certificates. The floor
 *  is gender-aware — per the official PDFs it differs between بنين and بنات
 *  (e.g. Basic Education: 70% males vs 75% females), so it is derived from
 *  the selected gender's departments rather than the shared college weights. */
export function getSpecialtyInfoAlert(college: College, gender?: Gender): StepAlert | null {
    const depts = departments.filter(
        (d) => d.collegeKey === college.key && (!gender || d.gender === gender),
    );
    const mins = depts
        .map((d) => d.minHighSchool ?? college.weights.minHighSchool)
        .filter((v): v is number => typeof v === "number");
    const minHs = mins.length > 0 ? Math.min(...mins) : college.weights.minHighSchool;
    if (minHs == null) {
        return null;
    }
    return {
        severity: "info",
        lines: [
            { key: "dream_university_high_school_min", params: { percent: minHs } },
            { key: "dream_university_paaet_recent_certificates_note" },
        ],
    };
}

/** PAAET shows no score-step info box (it relies on the shared gate alerts). */
export function getScoreInfoAlert(
    _college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    _collegeMinHs: number | null,
    _enteredHs: number,
): StepAlert | null {
    return null;
}

/** PAAET has no track minimum, so the results failure block is never reached;
 *  the generic lines are provided for completeness. */
export function getResultsFailureLines(
    college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    return [
        {
            key: "dream_university_below_accepted_rate_failure_line1",
            params: { minHs: college.weights.minHighSchool ?? trackMinHs ?? 0 },
        },
        {
            key: "dream_university_below_accepted_rate_failure_line2",
            params: { acceptedRate: minRate },
        },
    ];
}

export function getColleges(gender?: Gender, _track?: Track | null): College[] {
    return gender ? getCollegesForGender(gender) : colleges;
}

export function getDepartmentsForFilters(gender?: Gender, _track?: Track | null): Department[] {
    return gender ? departments.filter((d) => d.gender === gender) : departments;
}
